const markdownModules = import.meta.glob("../../content/blog/*.md", {
  query: "?raw",
  import: "default",
  eager: true,
}) as Record<string, string>;

const siteUrl = "https://reactemailbuilder.com";

function getPublishingDate() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/London",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

function isPublished(date?: string) {
  return !date || date <= getPublishingDate();
}

export type BlogListItem = {
  slug: string;
  title: string;
  description: string;
  category: string;
  readTime: string;
  href: string;
  date?: string;
  tags?: string[];
};

export type MarkdownBlogPost = BlogListItem & {
  body: string;
  html: string;
  tocItems: Array<{ id: string; text: string; level: number }>;
  canonical: string;
  ogImage: string;
};

const legacyBlogPosts: BlogListItem[] = [
  {
    slug: "best-react-email-templates",
    title: "Best React Email Templates for 2026 — 148 Free Examples by Brand",
    description:
      "Browse 148 production-ready React Email templates across 75 brands. From Airbnb booking confirmations to Stripe payment receipts — every template is free to preview and export.",
    category: "Template Guide",
    readTime: "12 min read",
    href: "/blog/best-react-email-templates",
  },
  {
    slug: "build-email-templates-with-ai",
    title: "How to Build Email Templates with AI — React Email Builder Guide",
    description:
      "Step-by-step guide to building production-ready email templates with AI. Generate React Email components from a prompt, preview live, and export HTML or JSX.",
    category: "How-to Guide",
    readTime: "8 min read",
    href: "/blog/build-email-templates-with-ai",
  },
  {
    slug: "react-email-vs-mjml",
    title: "React Email vs MJML — Which Should You Use in 2026?",
    description:
      "Detailed comparison of React Email and MJML for building email templates. Syntax, DX, testing, ecosystem, and when to pick each. Plus AI generation with React Email Builder.",
    category: "Comparison Guide",
    readTime: "10 min read",
    href: "/blog/react-email-vs-mjml",
  },
];

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function normalizeBoldWrappedLinks(value: string): string {
  return value.replace(/\*\*([^*]*\[[^\]]+\]\([^)]+\)[^*]*)\*\*/g, "$1");
}

function stripInlineMarkdown(value: string): string {
  return normalizeBoldWrappedLinks(value)
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1")
    .trim();
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "section";
}

function createHeadingIdFactory() {
  const counts = new Map<string, number>();

  return (value: string) => {
    const base = slugify(stripInlineMarkdown(value));
    const count = counts.get(base) ?? 0;
    counts.set(base, count + 1);
    return count === 0 ? base : `${base}-${count + 1}`;
  };
}

function renderInlineMarkdown(value: string): string {
  const normalizedValue = normalizeBoldWrappedLinks(value);
  const regex = /(`[^`]+`|\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g;
  const parts: string[] = [];
  let lastIndex = 0;

  for (const match of normalizedValue.matchAll(regex)) {
    const token = match[0];
    const index = match.index ?? 0;

    if (index > lastIndex) {
      parts.push(escapeHtml(value.slice(lastIndex, index)));
    }

    if (token.startsWith("`")) {
      parts.push(
        `<code class="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[0.95em] text-slate-900">${escapeHtml(token.slice(1, -1))}</code>`,
      );
    } else if (token.startsWith("**")) {
      parts.push(`<strong class="font-semibold text-slate-950">${escapeHtml(token.slice(2, -2))}</strong>`);
    } else {
      const linkMatch = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (linkMatch) {
        const [, label, href] = linkMatch;
        const external = /^https?:\/\//.test(href);
        const rel = external ? ' target="_blank" rel="noreferrer"' : "";
        parts.push(
          `<a href="${escapeHtml(href)}" class="font-medium text-sky-700 underline decoration-sky-300 underline-offset-4 transition hover:text-sky-800"${rel}>${escapeHtml(label)}</a>`,
        );
      }
    }

    lastIndex = index + token.length;
  }

  if (lastIndex < normalizedValue.length) {
    parts.push(escapeHtml(normalizedValue.slice(lastIndex)));
  }

  return parts.join("");
}

function parseFrontmatter(raw: string) {
  const match = raw.match(/^---\n([\s\S]*?)\n---\n?([\s\S]*)$/);
  if (!match) {
    throw new Error("Markdown post is missing frontmatter");
  }

  const [, frontmatterBlock, body] = match;
  const frontmatter: Record<string, string | string[]> = {};

  for (const line of frontmatterBlock.split("\n")) {
    const separatorIndex = line.indexOf(":");
    if (separatorIndex === -1) continue;
    const key = line.slice(0, separatorIndex).trim();
    const rawValue = line.slice(separatorIndex + 1).trim();

    if (rawValue.startsWith("[")) {
      frontmatter[key] = JSON.parse(rawValue);
      continue;
    }

    frontmatter[key] = rawValue.replace(/^"|"$/g, "");
  }

  return { frontmatter, body: body.trim() };
}

function extractHeadings(markdown: string) {
  const makeId = createHeadingIdFactory();
  const items: Array<{ id: string; text: string; level: number }> = [];

  for (const line of markdown.split("\n")) {
    const match = line.match(/^(#{2,3})\s+(.+)$/);
    if (!match) continue;
    const [, hashes, text] = match;
    items.push({ id: makeId(text), text: stripInlineMarkdown(text), level: hashes.length });
  }

  return items;
}

function markdownToHtml(markdown: string): string {
  const lines = markdown.replaceAll("\r\n", "\n").split("\n");
  const html: string[] = [];
  const makeHeadingId = createHeadingIdFactory();
  let index = 0;

  const isSpecialLine = (line: string) => {
    const trimmed = line.trim();
    return (
      trimmed.length === 0 ||
      trimmed.startsWith("```") ||
      /^(#{1,6})\s+/.test(trimmed) ||
      /^-\s+/.test(trimmed) ||
      /^\d+\.\s+/.test(trimmed) ||
      /^>\s?/.test(trimmed)
    );
  };

  while (index < lines.length) {
    const line = lines[index];
    const trimmed = line.trim();

    if (!trimmed) {
      index += 1;
      continue;
    }

    if (trimmed.startsWith("```")) {
      const language = trimmed.slice(3).trim();
      const block: string[] = [];
      index += 1;
      while (index < lines.length && !lines[index].trim().startsWith("```")) {
        block.push(lines[index]);
        index += 1;
      }
      index += 1;
      html.push(
        `<pre class="mt-6 overflow-x-auto rounded-md bg-slate-950 p-4 text-sm leading-6 text-slate-100 shadow-sm"><code${language ? ` data-language="${escapeHtml(language)}"` : ""}>${escapeHtml(block.join("\n"))}</code></pre>`,
      );
      continue;
    }

    const headingMatch = trimmed.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      const [, hashes, text] = headingMatch;
      const level = hashes.length;
      if (level === 1) {
        index += 1;
        continue;
      }
      const id = makeHeadingId(text);
      const className =
        level === 2
          ? "mt-10 scroll-mt-24 text-2xl font-semibold tracking-tight text-slate-950 sm:text-3xl"
          : "mt-8 scroll-mt-24 text-xl font-semibold text-slate-950";
      html.push(`<h${level} id="${id}" class="${className}">${renderInlineMarkdown(text)}</h${level}>`);
      index += 1;
      continue;
    }

    if (/^-\s+/.test(trimmed)) {
      const items: string[] = [];
      while (index < lines.length && /^-\s+/.test(lines[index].trim())) {
        items.push(lines[index].trim().replace(/^-\s+/, ""));
        index += 1;
      }
      html.push(
        `<ul class="mt-6 space-y-3 pl-6 text-base leading-8 text-slate-700 list-disc">${items
          .map((item) => `<li>${renderInlineMarkdown(item)}</li>`)
          .join("")}</ul>`,
      );
      continue;
    }

    if (/^\d+\.\s+/.test(trimmed)) {
      const items: string[] = [];
      while (index < lines.length && /^\d+\.\s+/.test(lines[index].trim())) {
        items.push(lines[index].trim().replace(/^\d+\.\s+/, ""));
        index += 1;
      }
      html.push(
        `<ol class="mt-6 space-y-3 pl-6 text-base leading-8 text-slate-700 list-decimal">${items
          .map((item) => `<li>${renderInlineMarkdown(item)}</li>`)
          .join("")}</ol>`,
      );
      continue;
    }

    if (/^>\s?/.test(trimmed)) {
      const quote: string[] = [];
      while (index < lines.length && /^>\s?/.test(lines[index].trim())) {
        quote.push(lines[index].trim().replace(/^>\s?/, ""));
        index += 1;
      }
      html.push(
        `<blockquote class="mt-6 border-l-4 border-sky-200 bg-sky-50/50 px-5 py-4 text-base leading-8 text-slate-700">${renderInlineMarkdown(quote.join(" "))}</blockquote>`,
      );
      continue;
    }

    const paragraph: string[] = [trimmed];
    index += 1;
    while (index < lines.length && !isSpecialLine(lines[index])) {
      paragraph.push(lines[index].trim());
      index += 1;
    }

    html.push(`<p class="mt-6 text-lg leading-8 text-slate-700">${renderInlineMarkdown(paragraph.join(" "))}</p>`);
  }

  return html.join("\n");
}

function readTimeFor(body: string): string {
  const words = body.split(/\s+/).filter(Boolean).length;
  return `${Math.max(1, Math.ceil(words / 220))} min read`;
}

function inferCategory(tags: string[] | undefined): string {
  if (!tags?.length) return "How-to Guide";
  if (tags.some((tag) => tag.includes("comparison"))) return "Comparison Guide";
  if (tags.some((tag) => tag.includes("template"))) return "Provider Guide";
  return "How-to Guide";
}

function normalizeOgImage(value: string | undefined): string {
  if (!value) return `${siteUrl}/og-image.png`;
  if (/^https?:\/\//.test(value)) return value;
  return `${siteUrl}${value.startsWith("/") ? value : `/${value}`}`;
}

function slugFromPath(path: string): string {
  const filename = path.split("/").pop()?.replace(/\.md$/, "") ?? "";
  return filename.replace(/^\d{4}-\d{2}-\d{2}-/, "");
}

const markdownBlogPosts: MarkdownBlogPost[] = Object.entries(markdownModules)
  .map(([path, raw]) => {
    const slug = slugFromPath(path);
    const { frontmatter, body } = parseFrontmatter(raw);
    const title = String(frontmatter.title ?? slug);
    const description = String(frontmatter.description ?? "");
    const tags = Array.isArray(frontmatter.tags) ? frontmatter.tags.map(String) : [];
    const href = `/blog/${slug}`;

    return {
      slug,
      title,
      description,
      category: inferCategory(tags),
      readTime: readTimeFor(body),
      href,
      date: String(frontmatter.date ?? ""),
      tags,
      body,
      html: markdownToHtml(body),
      tocItems: extractHeadings(body),
      canonical: String(frontmatter.canonical ?? `${siteUrl}${href}`),
      ogImage: normalizeOgImage(typeof frontmatter.ogImage === "string" ? frontmatter.ogImage : undefined),
    };
  })
  .filter((post) => isPublished(post.date))
  .sort((a, b) => (a.date && b.date ? b.date.localeCompare(a.date) : a.title.localeCompare(b.title)));

const allBlogPosts: BlogListItem[] = [...markdownBlogPosts, ...legacyBlogPosts];

export function getMarkdownBlogPosts() {
  return markdownBlogPosts;
}

export function getMarkdownBlogPostBySlug(slug: string) {
  return markdownBlogPosts.find((post) => post.slug === slug);
}

export function getAllBlogPosts() {
  return allBlogPosts;
}

export function getRelatedBlogPosts(excludeHref: string, limit = 2) {
  return allBlogPosts.filter((post) => post.href !== excludeHref).slice(0, limit);
}
